import { forwardRef } from "react";
import type { HTMLAttributes } from "react";
import "./SegmentedStepper.css";

export type SegmentedStepperSize = "sm" | "md";

export interface SegmentedStepperProps extends HTMLAttributes<HTMLDivElement> {
  /** Total number of segments to render. */
  total: number;
  /** Number of completed (filled) segments, counted from the start. */
  current: number;
  /** Bar sizing. Defaults to "md". */
  size?: SegmentedStepperSize;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

/**
 * A compact, segmented linear progress indicator — a row of rounded bars where the first
 * `current` segments are filled. Useful for multi-step funnels where a labelled `Stepper`
 * would be too heavy (e.g. a slim header progress indicator).
 */
export const SegmentedStepper = forwardRef<HTMLDivElement, SegmentedStepperProps>(
  function SegmentedStepper({ total, current, size = "md", className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cx(
          "go4-segmented-stepper",
          size === "sm" && "go4-segmented-stepper--sm",
          className,
        )}
        role="progressbar"
        aria-valuenow={current}
        aria-valuemin={0}
        aria-valuemax={total}
        {...rest}
      >
        {Array.from({ length: total }, (_, index) => (
          <span
            key={index}
            className={cx(
              "go4-segmented-stepper__bar",
              index < current && "go4-segmented-stepper__bar--filled",
            )}
          />
        ))}
      </div>
    );
  },
);
